import { useEffect, useMemo, useCallback, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DndContext, DragOverlay, pointerWithin, rectIntersection, type CollisionDetection } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import Portal from '@ui/portal/portal';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import Button from '@ui/buttons/button/button';
import { ICONS } from '@ui/icons/types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getWorkingProject, selectWorkingProject, selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import { loadEventScheduledEmails } from '@/store/slices/scheduled-emails';
import {
  deleteBlock,
  loadCampaignEditor,
  moveBlock,
  resetCampaignEditor,
  setCampaignEditorField,
  setCampaignEditorTemplateMounted,
  setSelectedBlockId,
  updateBlockImage,
  updateBlockValue,
} from '@/store/slices/campaign-editor';
import {
  selectCampaignEditorBlocks,
  selectCampaignEditorFields,
  selectCampaignEditorTheme,
  selectCampaignEditorThemeMode,
  selectSelectedBlockId,
  selectSortableBlockIds,
} from '@/store/slices/campaign-editor/selectors';
import {
  selectScheduledEmailByUuid,
  selectLoadingScheduledEmails,
  selectErrorLoadingScheduledEmails,
} from '@/store/slices/scheduled-emails/selectors';
import { scheduledEmailToCampaign } from '@/utils/scheduled-email-campaign';
import { loadEmailTranslations } from '@/utils/email-translations';
import { campaignTemplatesMap } from '../templates';
import { templateStaticDefaults } from '../templates/template-defaults';
import { resolveThemedColorHex } from '../templates/utils/utils';
import classes from './campaign-editor.module.css';
import PillTabs from '@ui/tabs/pill-tabs';
import classNames from 'classnames';
import { PreviewSize, TEMPLATE_WIDTH } from './constants';
import { useLoadAllAttendeesList } from '../../audiences/hooks/listsv2/use-load-all-attendees-list';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { EColorVariablesV2, EPaletteModes } from '@/types/theme-packs.types';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import ContentBlocksPanel, { WidgetTilePreview } from './components/panels/content-blocks-panel';
import TemplateStrip from './components/template-strip';
import ScheduleEmailModal from './components/schedule-email-modal';
import CampaignsThemeTray from './components/campaigns-theme-tray';
import { usePreviewScale } from './hooks/use-preview-scale';
import { useWidgetDragAndDrop } from './hooks/use-widget-drag-and-drop';
import { useCampaignPublishActions } from './hooks/use-campaign-publish-actions';
import type { CampaignTemplateTypes } from '../templates/campaign-template.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import { EmailTypeEnum } from '@/types/email.types';
import QuillInactiveProvider from '@ui/quill/quill-inactive-provider';
import type { CampaignEditorFields } from '@/store/slices/campaign-editor/types';

// pointerWithin is size-independent, unlike the default rectIntersection; falls back for gaps
const collisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
};

export default function CampaignEditor() {
  const { campaignUuid, projectUuid, language } = useParams<{
    campaignUuid: string;
    projectUuid: string;
    language: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  useLoadAllAttendeesList();

  const workingProject = useAppSelector(selectWorkingProject);
  const projectUuidFromStore = useAppSelector(selectWorkingProjectUuid);
  const errorLoadingScheduledEmails = useAppSelector(selectErrorLoadingScheduledEmails);
  const loadingEmails = useAppSelector(selectLoadingScheduledEmails);
  const event = useAppSelector(getWorkingProject);
  const themeMode = useAppSelector(selectCampaignEditorThemeMode) ?? EPaletteModes.Light;
  const emailSelector = useMemo(() => selectScheduledEmailByUuid(campaignUuid), [campaignUuid]);
  const scheduledEmail = useAppSelector(emailSelector);

  const [previewSize, setPreviewSize] = useState<PreviewSize>('desktop');
  const [showContentBlocksPanel, setShowContentBlocksPanel] = useState(true);
  const [tab, setTab] = useState<'Edit' | 'Preview'>('Edit');
  const [showTemplateStrip, setShowTemplateStrip] = useState(false);
  const templateStripVisibleTimeout = useRef<NodeJS.Timeout | null>(null);
  const [templateStripVisible, setTemplateStripVisible] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [privacyPolicyLabel, setPrivacyPolicyLabel] = useState<string>();
  const [blink, setBlink] = useState(false);

  // the footer's legal-link text, in the language being edited
  useEffect(() => {
    if (!language) return;
    let cancelled = false;
    loadEmailTranslations([language]).then((translations) => {
      if (!cancelled) setPrivacyPolicyLabel(translations.get(language)?.PrivacyPolicy);
    });
    return () => {
      cancelled = true;
    };
  }, [language]);

  const templateContainer = useRef<HTMLDivElement | null>(null);
  // the sync back into the scheduled email waits until the template has mounted
  const setTemplateContainer = useCallback(
    (node: HTMLElement | null) => {
      templateContainer.current = node as HTMLDivElement | null;
      dispatch(setCampaignEditorTemplateMounted(!!node));
    },
    [dispatch]
  );

  const campaign = useMemo(() => (scheduledEmail ? scheduledEmailToCampaign(scheduledEmail) : null), [scheduledEmail]);
  const currentTemplateId: CampaignTemplateTypes | undefined =
    (scheduledEmail?.working_customizations?.v2.templateId as CampaignTemplateTypes | undefined) ??
    (campaign?.template_id as CampaignTemplateTypes | undefined);
  const eventTimezone = workingProject?.settings.canonicalTimezone;

  const TemplateComponent = useMemo(() => {
    if (currentTemplateId) {
      return campaignTemplatesMap.get(currentTemplateId);
    }

    return undefined;
  }, [currentTemplateId]);

  const editorInitialized = useAppSelector(
    (state) =>
      state.campaignEditor.initialized &&
      state.campaignEditor.campaignUuid === campaignUuid &&
      state.campaignEditor.language === language
  );
  const fields = useAppSelector(selectCampaignEditorFields);
  const {
    headingFont,
    bodyFont,
    salutation,
    title,
    startIntro,
    cta,
    mainImage,
    headingImage,
    description,
    backgroundColor,
    containerColor,
    bodyTextColor,
    headingTextColor,
    accentColor,
    secondaryAccentColor,
    secondaryBackgroundColor,
    buttonTextColor,
    hideBrandliveLogo,
  } = fields;
  const theme = useAppSelector(selectCampaignEditorTheme);
  const blocks = useAppSelector(selectCampaignEditorBlocks);
  const selectedBlockId = useAppSelector(selectSelectedBlockId);
  const sortableBlockIds = useAppSelector(selectSortableBlockIds);

  // seeds the editor from the stored email, again whenever the campaign or the language changes
  useEffect(() => {
    if (scheduledEmail && campaignUuid && language && !editorInitialized) {
      dispatch(loadCampaignEditor({ campaignUuid, language }));
      setBlink(true);
      setTimeout(() => setBlink(false));
    }
  }, [scheduledEmail, campaignUuid, language, editorInitialized, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetCampaignEditor());
    };
  }, [dispatch]);

  const fieldSetters = useMemo(() => {
    const setter =
      <K extends 'salutation' | 'title' | 'cta' | 'description' | 'startIntro' | 'headingImage' | 'mainImage'>(
        key: K
      ) =>
      (value: CampaignEditorFields[K]) =>
        dispatch(setCampaignEditorField({ key, value } as Parameters<typeof setCampaignEditorField>[0]));
    return {
      setSalutation: setter('salutation'),
      setTitle: setter('title'),
      setCta: setter('cta'),
      setDescription: setter('description'),
      setStartIntro: setter('startIntro'),
      setHeadingImage: setter('headingImage'),
      setMainImage: setter('mainImage'),
    };
  }, [dispatch]);

  const blockHandlers = useMemo(
    () => ({
      onUpdateBlockValue: (blockId: string, value: string) => dispatch(updateBlockValue({ blockId, value })),
      onUpdateBlockImage: (blockId: string, src: string) => dispatch(updateBlockImage({ blockId, src })),
      onDeleteBlock: (blockId: string) => dispatch(deleteBlock(blockId)),
      onMoveBlock: (blockId: string, direction: 'up' | 'down') => dispatch(moveBlock({ blockId, direction })),
      onSelectBlock: (blockId: string | null) => dispatch(setSelectedBlockId(blockId)),
    }),
    [dispatch]
  );

  const currentTemplateDefaults = templateStaticDefaults[currentTemplateId ?? 'capri'] ?? templateStaticDefaults.capri;
  const themeColors = theme?.colors?.[themeMode] ?? theme?.colors;
  const backdropColor = resolveThemedColorHex(
    backgroundColor,
    themeColors?.backgroundColor,
    currentTemplateDefaults.backgroundColor || '#FFFFFF'
  );
  const pageColor = resolveThemedColorHex(
    containerColor,
    themeColors?.containerColor,
    currentTemplateDefaults.containerColor || '#FFFFFF'
  );

  const {
    dndSensors,
    setPreviewDropRef,
    isPreviewDropOver,
    draggedWidget,
    isDragActive,
    handleWidgetDragStart,
    handleWidgetDragEnd,
  } = useWidgetDragAndDrop({ isDropEnabled: tab === 'Edit' });

  const { containerRef, scale } = usePreviewScale(
    previewSize,
    templateContainer,
    TemplateComponent,
    event,
    theme,
    isDragActive
  );

  useEffect(() => {
    if (!scheduledEmail && !loadingEmails && projectUuidFromStore && !errorLoadingScheduledEmails) {
      dispatch(loadEventScheduledEmails(projectUuidFromStore));
    }
  }, [scheduledEmail, loadingEmails, projectUuidFromStore, dispatch, errorLoadingScheduledEmails]);

  const handleBack = useCallback(() => {
    navigate(`/project/edit/${projectUuid}/${language}/campaigns`);
  }, [language, navigate, projectUuid]);

  const { updatingEmail, sendingTest, handlePublish, sendTest } = useCampaignPublishActions({
    scheduledEmail,
    workingProject,
    onPublished: handleBack,
  });

  const toggleTemplateStrip = useCallback(() => {
    if (templateStripVisibleTimeout.current) {
      clearTimeout(templateStripVisibleTimeout.current);
    }

    if (showTemplateStrip) {
      templateStripVisibleTimeout.current = setTimeout(() => {
        setTemplateStripVisible(false);
      }, 300);
    } else {
      setTemplateStripVisible(true);
    }

    setShowTemplateStrip(!showTemplateStrip);
  }, [showTemplateStrip]);

  const hideTemplateStrip = useCallback(() => {
    if (templateStripVisibleTimeout.current) {
      clearTimeout(templateStripVisibleTimeout.current);
    }

    templateStripVisibleTimeout.current = setTimeout(() => {
      setTemplateStripVisible(false);
    }, 300);
    setShowTemplateStrip(false);
  }, []);

  return (
    <Portal className={classNames(classes.fullScreen, 'position-fixed top-0 left-0 z-100')}>
      <Container
        className="flex-column width-100-percent height-100-percent overflow-hidden"
        background="var(--BG, #1a1a1f)"
      >
        <Container
          tagName="header"
          className="flex ai-center gap-16 height-63 min-height-63 padding-h-20"
          background="var(--BG, #1a1a1f)"
        >
          <Container className="flex ai-center gap-12 min-width-180">
            <Button
              clear
              className="ai-center jc-center"
              width="32px"
              height="32px"
              borderRadius="8px"
              hoverBackgroundColor="var(--gray-900)"
              onClick={handleBack}
            >
              <Icon name={ICONS.ARROW_LEFT} size={20} color="var(--global-ico-text-2)" />
            </Button>
            <Text className="H4" color="var(--global-ico-text-1)">
              {campaign?.title || 'Campaign'}
            </Text>
            <CampaignsThemeTray />
          </Container>

          <Container className="flex-1" />

          <Container className="flex ai-center jc-flex-end gap-8 min-width-180">
            {/* registration emails are transactional - you can't schedule them */}
            <OptionalComponent display={scheduledEmail?.email_type !== EmailTypeEnum.event_registration_email}>
              <Button onClick={() => setScheduleOpen(true)} designSize="small" design="LocalBttnSecHalfTransp">
                <Text className="L2 flex ai-center gap-6">
                  <Icon name={ICONS.CLOCK} size={12} /> Schedule
                </Text>
              </Button>
            </OptionalComponent>

            <Button loading={sendingTest} onClick={sendTest} designSize="small" design="LocalBttnSecHalfTransp">
              <Text className="L2 flex ai-center gap-6">
                <Icon name={ICONS.EMAIL} size={12} />
                Send Test
              </Text>
            </Button>

            <Button
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
              color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              borderRadius="100px"
              onClick={handlePublish}
              disabled={updatingEmail || !scheduledEmail}
              loading={updatingEmail}
              designSize="small"
            >
              <Text
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                className="L2 flex ai-center gap-8"
              >
                Save & Publish
                <Icon name={ICONS.ARROW_RIGHT} size={12} />
              </Text>
            </Button>
          </Container>
        </Container>

        <Container className="flex-column width-100-percent">
          <Container
            className="position-relative overflow-hidden"
            maxHeight={showTemplateStrip ? '350px' : '0'}
            opacity={showTemplateStrip ? 1 : 0}
            style={{ transform: showTemplateStrip ? 'translateY(0)' : 'translateY(-8px)' }}
            transition="max-height 250ms ease, opacity 200ms ease, transform 250ms ease"
          >
            <QuillInactiveProvider>
              <TemplateStrip timezone={eventTimezone} hidden={!templateStripVisible} onClose={hideTemplateStrip} />
            </QuillInactiveProvider>
          </Container>

          <Container
            className={classNames('grid ai-center gap-16 padding-v-8 padding-h-16', classes.templateSubHeaderRow)}
            transition="250ms grid-template-columns"
          >
            <Button
              designSize="small"
              width="fit-content"
              design="LocalBttnSecHalfTransp"
              onClick={toggleTemplateStrip}
            >
              <Text className="L2 flex ai-center gap-6">
                Change Template
                <Icon name={showTemplateStrip ? ICONS.KEYBOARD_ARROW_UP : ICONS.KEYBOARD_ARROW_DOWN} size={12} />
              </Text>
            </Button>

            <Container className="grid ai-center gap-8" gridTemplateColumns="1fr auto 1fr">
              <Container />
              <PillTabs tabs={['Edit', 'Preview']} value={tab} onChange={setTab} />
              <Container className="flex ai-center js-flex-end gap-2 padding-2 bg-color-gray-900 radius-100">
                <Button
                  clear
                  className="ai-center jc-center"
                  width="28px"
                  height="28px"
                  borderRadius="50%"
                  backgroundColor={previewSize === 'desktop' ? 'var(--gray-700)' : 'transparent'}
                  hoverBackgroundColor="var(--gray-800)"
                  onClick={() => setPreviewSize('desktop')}
                  title="Desktop"
                >
                  <Icon name={ICONS.DESKTOP} size={16} color="var(--global-ico-text-2)" />
                </Button>
                <Button
                  clear
                  className="ai-center jc-center"
                  width="28px"
                  height="28px"
                  borderRadius="50%"
                  backgroundColor={previewSize === 'tablet' ? 'var(--gray-700)' : 'transparent'}
                  hoverBackgroundColor="var(--gray-800)"
                  onClick={() => setPreviewSize('tablet')}
                  title="Tablet"
                >
                  <Icon name={ICONS.TABLET} size={15} color="var(--global-ico-text-2)" />
                </Button>
                <Button
                  clear
                  className="ai-center jc-center"
                  width="28px"
                  height="28px"
                  borderRadius="50%"
                  backgroundColor={previewSize === 'mobile' ? 'var(--gray-700)' : 'transparent'}
                  hoverBackgroundColor="var(--gray-800)"
                  onClick={() => setPreviewSize('mobile')}
                  title="Mobile"
                >
                  <Icon name={ICONS.MOBILE} size={13} color="var(--global-ico-text-2)" />
                </Button>
              </Container>
            </Container>
          </Container>
        </Container>

        <DndContext
          sensors={dndSensors}
          collisionDetection={collisionDetection}
          onDragStart={handleWidgetDragStart}
          onDragEnd={handleWidgetDragEnd}
        >
          <Container
            className={classNames('flex-1 min-height-0 grid overflow-hidden padding-16 gap-16', classes.body, {
              [classes.bodyNoPanel]: !(showContentBlocksPanel && tab === 'Edit'),
            })}
            transition="250ms grid-template-columns"
          >
            {showContentBlocksPanel && tab === 'Edit' && (
              <Container transition="transform 250ms, opacity 150ms">
                <Container
                  className="flex-column height-100-percent min-height-0 overflow-hidden radius-16"
                  width="340px"
                  minWidth="340px"
                  background="var(--global-cont-1)"
                >
                  <ContentBlocksPanel
                    onClose={() => setShowContentBlocksPanel(false)}
                    isEditMode={tab === 'Edit'}
                    backdropColor={backdropColor}
                    pageColor={pageColor}
                    timezone={eventTimezone}
                  />
                </Container>
              </Container>
            )}
            <main
              ref={setPreviewDropRef}
              className={classNames(
                'flex-1 flex-column min-width-0 min-height-0 overflow-hidden radius-16',
                classes.previewArea,
                { [classes.previewAreaDropActive]: isPreviewDropOver }
              )}
              style={{
                backgroundColor: backdropColor,
              }}
            >
              <Container
                className="flex-1 flex jc-center overflow-y-auto overflow-x-hidden padding-v-32 padding-h-24 radius-16"
                ref={containerRef}
                transition="background-color 350ms"
              >
                {event && TemplateComponent ? (
                  <Container
                    className={classes.previewScaleWrapper}
                    style={{ '--preview-scale': scale } as CSSProperties}
                    transition="200ms transform"
                  >
                    <Container className="padding-b-32" width={`${TEMPLATE_WIDTH}px`} ref={setTemplateContainer}>
                      <SortableContext items={sortableBlockIds} strategy={verticalListSortingStrategy}>
                        {!blink && (
                          <TemplateComponent
                            event={event}
                            theme={theme}
                            mode={themeMode ?? undefined}
                            timezone={eventTimezone}
                            href={`${import.meta.env.VITE_URL_SHORTENER}/${event.uuid}{{reg_code}}`}
                            salutation={salutation || undefined}
                            setSalutation={tab === 'Preview' ? undefined : fieldSetters.setSalutation}
                            title={title || undefined}
                            setTitle={tab === 'Preview' ? undefined : fieldSetters.setTitle}
                            cta={cta || undefined}
                            setCta={tab === 'Preview' ? undefined : fieldSetters.setCta}
                            description={description || undefined}
                            setDescription={tab === 'Preview' ? undefined : fieldSetters.setDescription}
                            startIntro={startIntro || undefined}
                            setStartIntro={tab === 'Preview' ? undefined : fieldSetters.setStartIntro}
                            setHeaderImage={tab === 'Preview' ? undefined : fieldSetters.setHeadingImage}
                            setMainImage={tab === 'Preview' ? undefined : fieldSetters.setMainImage}
                            blocks={blocks ?? undefined}
                            hideBrandliveLogo={hideBrandliveLogo}
                            privacyPolicyLabel={privacyPolicyLabel}
                            onUpdateBlockValue={tab === 'Preview' ? undefined : blockHandlers.onUpdateBlockValue}
                            onUpdateBlockImage={tab === 'Preview' ? undefined : blockHandlers.onUpdateBlockImage}
                            onDeleteBlock={tab === 'Preview' ? undefined : blockHandlers.onDeleteBlock}
                            onMoveBlock={tab === 'Preview' ? undefined : blockHandlers.onMoveBlock}
                            selectedBlockId={selectedBlockId}
                            onSelectBlock={tab === 'Preview' ? undefined : blockHandlers.onSelectBlock}
                            isEditMode={tab === 'Edit'}
                            customizations={{
                              title,
                              description,
                              headingFont,
                              bodyFont,
                              salutation,
                              startIntro,
                              cta,
                              mainImage,
                              headingImage,
                              backgroundColor,
                              containerColor,
                              secondaryAccentColor,
                              secondaryBackgroundColor,
                              accentColor,
                              headingTextColor,
                              bodyTextColor,
                              buttonTextColor,
                              secondaryImage: mainImage,
                              subject: title,
                            }}
                          />
                        )}
                      </SortableContext>
                    </Container>
                  </Container>
                ) : (
                  <Container className="flex ai-center jc-center width-100-percent">
                    <Text className="L1" design="GlobalIcoText3">
                      {TemplateComponent ? 'Loading preview...' : 'No template selected'}
                    </Text>
                  </Container>
                )}
              </Container>
            </main>
          </Container>
          <DragOverlay>{draggedWidget ? <WidgetTilePreview widget={draggedWidget} /> : null}</DragOverlay>
        </DndContext>

        <ScheduleEmailModal
          open={scheduleOpen}
          onClose={() => setScheduleOpen(false)}
          scheduledEmail={scheduledEmail}
        />
      </Container>
    </Portal>
  );
}
